import { makeStyles } from "@material-ui/core/styles";

export const useStyles = makeStyles((theme) => ({
  editorContainer: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 120px)",
    width: "100%",
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down("xs")]: {
      height: "calc(100vh - 56px)",
    },
  },
  toolbar: {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    flexWrap: "nowrap",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.75, 1),
    },
  },
  content: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    position: "relative",
  },
  // Sidebar desktop/tablet
  sidebar: {
    width: 250,
    flexShrink: 0,
    padding: theme.spacing(1.5),
    borderRight: `1px solid ${theme.palette.divider}`,
    overflowY: "auto",
    backgroundColor: theme.palette.background.paper,
    transition: "width 0.2s ease",
    [theme.breakpoints.down("sm")]: {
      width: 200,
      padding: theme.spacing(1),
    },
    // On xs (mobile) the sidebar renders in a Drawer — hide inline
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  // Inner content of sidebar (reused in Drawer on mobile)
  // Paleta plegada (0422 QA-UX): una columna de iconos; el nombre sale en el tooltip.
  sidebarPlegado: {
    width: 64,
    padding: theme.spacing(1, 0.75),
    [theme.breakpoints.down("sm")]: {
      width: 58,
      padding: theme.spacing(1, 0.5),
    },
  },
  paletteHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  paletteHeaderCompacta: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(0.5),
  },
  paletteHint: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  paletteDivider: {
    margin: theme.spacing(1, 0),
  },
  nodeCardCompacta: {
    marginBottom: theme.spacing(0.75),
    cursor: "grab",
  },
  nodeCardContentCompacta: {
    display: "flex",
    justifyContent: "center",
    padding: `${theme.spacing(1)}px !important`,
  },
  sidebarContent: {
    width: 220,
    padding: theme.spacing(1.5),
    overflowY: "auto",
    height: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  reactFlowWrapper: {
    flex: 1,
    height: "100%",
    position: "relative",
    backgroundColor: theme.palette.background.default,
    // Resaltado del recorrido durante la previsualización (0383 FS-2.7). Se aplica por
    // className del nodo para no tocar el `data` de los nodos, que es lo que se persiste.
    "& .react-flow__node.sim-active": {
      filter: "drop-shadow(0 0 10px rgba(76, 175, 80, 0.9))",
      outline: `3px solid ${theme.palette.success.main}`,
      outlineOffset: 3,
      borderRadius: 10,
      zIndex: 20,
    },
    "& .react-flow__node.sim-visited": {
      outline: `2px solid ${theme.palette.success.light}`,
      outlineOffset: 2,
      borderRadius: 10,
    },
    "& .react-flow__node.sim-dimmed": {
      opacity: 0.45,
    },

    /**
     * ── CONECTAR NODOS (0419 F3-UX) ────────────────────────────────────────────────────
     *
     * El problema medido: el punto visible es de 11 px. Para engancharlo hay que acertar con
     * el raton en un area minuscula, y mientras arrastras nada te dice si vas a acertar.
     *
     * Se resuelve en dos planos separados a proposito:
     *
     *   - El AREA AGARRABLE crece con un pseudo-elemento. No se agranda el punto porque el
     *     pseudo-elemento NO cambia la caja del elemento, y React Flow mide esa caja para
     *     decidir donde ancla la arista: agrandar el punto moveria las lineas de sitio.
     *   - La SENAL VISUAL solo aparece mientras se arrastra, para no llenar el lienzo de
     *     circulos gordos en reposo.
     */
    "& .react-flow__handle": {
      transition: "transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease",
    },
    "& .react-flow__handle::before": {
      content: '""',
      position: "absolute",
      top: -10,
      right: -10,
      bottom: -10,
      left: -10,
      borderRadius: "50%",
    },

    // En reposo las salidas se atenuan: el lienzo se lee mejor sin doce puntos gritando.
    "& .react-flow__node:not(:hover) .react-flow__handle": {
      opacity: 0.55,
    },
    "& .react-flow__node:hover .react-flow__handle": {
      opacity: 1,
      transform: "scale(1.25)",
    },

    /**
     * Mientras se arrastra una conexion: TODOS los destinos se agrandan y se iluminan. Es lo
     * que convierte "acertar en un punto" en "soltar cerca del recuadro".
     */
    "&.conectando .react-flow__handle": {
      opacity: 1,
      transform: "scale(1.6)",
    },
    "&.conectando .react-flow__handle::before": {
      top: -22,
      right: -22,
      bottom: -22,
      left: -22,
    },
    /**
     * Los conectores del tipo que NO sirve se apartan (0422 QA-UX): dejan pasar el ratón para que
     * al soltar se encuentre el del tipo correcto que está debajo, y se atenúan para que se vea
     * dónde sí se puede soltar. Ver `buscando` en FlowEditorReactFlow.
     */
    "&.busca-entrada .react-flow__handle.source, &.busca-salida .react-flow__handle.target": {
      pointerEvents: "none",
      opacity: 0.15,
      transform: "scale(1)",
    },
    "&.busca-entrada .react-flow__handle.source::before, &.busca-salida .react-flow__handle.target::before": {
      display: "none",
    },
    "&.conectando .react-flow__node": {
      outline: `2px dashed ${theme.palette.primary.main}55`,
      outlineOffset: 4,
      borderRadius: 12,
    },

    // El destino bajo el cursor. React Flow pone esta clase el solo.
    "& .react-flow__handle.react-flow__handle-valid": {
      transform: "scale(2.2)",
      background: theme.palette.success.main,
      borderColor: theme.palette.success.main,
      boxShadow: `0 0 0 6px ${theme.palette.success.main}44, 0 0 14px ${theme.palette.success.main}`,
    },
    /**
     * ── ARISTAS: SELECCION Y RECONEXION (0419 F3-UX) ───────────────────────────────────
     * Antes no habia forma de saber si una arista estaba seleccionada. Ahora se engorda, se
     * tine de acento y deja de estar en gris; y al pasar por encima ya avisa de que se puede
     * agarrar, que es lo que invita a arrastrar el extremo.
     */
    // El trazo en reposo se define AQUÍ, no en línea en cada arista: si viniera en línea, ganaría
    // al hover y a «seleccionada» y nunca se verían (0422 QA-UX, ver ButtonEdge).
    "& .react-flow__edge .react-flow__edge-path": {
      stroke: theme.palette.type === "dark" ? "#757575" : "#b1b1b7",
      strokeWidth: 3,
      transition: "stroke 120ms ease, stroke-width 120ms ease",
    },

    // Botón ✕ de la arista: blanco con borde gris; rojo al pasar por encima o si está seleccionada.
    "& .edge-delete-btn .edge-delete-fondo": {
      fill: "#fff",
      stroke: "#bbb",
      transition: "fill 120ms ease, stroke 120ms ease",
    },
    "& .edge-delete-btn .edge-delete-x": {
      fill: "#e53935",
      transition: "fill 120ms ease",
    },
    "& .edge-delete-btn:hover .edge-delete-fondo, & .edge-delete-btn.seleccionada .edge-delete-fondo": {
      fill: "#e53935",
      stroke: "#e53935",
    },
    "& .edge-delete-btn:hover .edge-delete-x, & .edge-delete-btn.seleccionada .edge-delete-x": {
      fill: "#fff",
    },
    "& .react-flow__edge:hover .react-flow__edge-path": {
      stroke: theme.palette.primary.light,
      strokeWidth: 4,
    },
    "& .react-flow__edge.selected .react-flow__edge-path": {
      stroke: theme.palette.primary.main,
      strokeWidth: 5,
      filter: `drop-shadow(0 0 6px ${theme.palette.primary.main}88)`,
    },
    // Los anclajes de los extremos son circulos transparentes: sin cursor no se adivinan.
    "& .react-flow__edgeupdater": {
      cursor: "grab",
    },
    "& .react-flow__edge:hover .react-flow__edgeupdater": {
      fill: `${theme.palette.primary.main}33`,
      stroke: theme.palette.primary.main,
      strokeWidth: 1.5,
    },

    "& .react-flow__handle.react-flow__handle-connecting": {
      background: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
  },
  simLockBanner: {
    position: "absolute",
    top: 12,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 15,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 20,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(33,150,243,0.18)" : "#e3f2fd",
    border: `1px solid ${theme.palette.info.main}`,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[2],
    pointerEvents: "none",
  },
  // Floating toggle button — visible only on mobile
  sidebarToggleFab: {
    position: "absolute",
    top: 10,
    left: 10,
    zIndex: 5,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[2],
    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },
  nodeCard: {
    marginBottom: theme.spacing(1),
    // «grab»: la tarjeta se puede arrastrar al lienzo (0422 QA-UX); el clic sigue funcionando.
    cursor: "grab",
    transition: "background-color 0.15s ease",
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  nodeCardContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: `${theme.spacing(1.25)} ${theme.spacing(1.5)} !important`,
    [theme.breakpoints.down("sm")]: {
      padding: `${theme.spacing(1)} ${theme.spacing(1.25)} !important`,
    },
  },
  nodeIcon: {
    fontSize: 20,
    [theme.breakpoints.down("sm")]: {
      fontSize: 17,
    },
  },
  nodeLabel: {
    fontWeight: 500,
    fontSize: 14,
    color: theme.palette.text.primary,
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
    },
  },
  nodeBadge: {
    display: "none",
  },
  categoryTitle: {
    fontWeight: 600,
    fontSize: 12,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    "&:first-of-type": {
      marginTop: 0,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: 10,
    },
  },
}));
